import { GroupId } from './systemConfig';

/**
 * Canonical Tier System (16 Tiers)
 *
 * TIER ↔ NIVEAU SYNC (Source of Truth)
 *
 * Tier  1 = B6    Begin Groep 6
 * Tier  2 = B6+   Begin Groep 6 Plus
 * Tier  3 = M6    Midden Groep 6
 * Tier  4 = M6+   Midden Groep 6 Plus
 * Tier  5 = E6    Eind Groep 6
 * Tier  6 = E6+   Eind Groep 6 Plus
 * Tier  7 = B7    Begin Groep 7
 * Tier  8 = B7+   Begin Groep 7 Plus
 * Tier  9 = M7    Midden Groep 7
 * Tier 10 = M7+   Midden Groep 7 Plus
 * Tier 11 = E7    Eind Groep 7
 * Tier 12 = E7+   Eind Groep 7 Plus
 * Tier 13 = B8    Begin Groep 8
 * Tier 14 = M8    Midden Groep 8
 * Tier 15 = E8    Eind Groep 8 (1F)
 * Tier 16 = E8+   Groep 8 Plus (2F/S)
 *
 * Group 8 has 4 tiers (not 6) because the plus-variants
 * for B8 and M8 are collapsed into the main tiers.
 */

export interface TierConfig {
  tier: number;
  levelCode: string;
  group: GroupId;
  period: 'B' | 'M' | 'E'; // Begin, Midden, Eind
  isPlus: boolean;
  title: string;
}
export const TIERS: TierConfig[] = [
// GROEP 6 (tiers 1–6)
{
  tier: 1,
  levelCode: 'B6',
  group: 6,
  period: 'B',
  isPlus: false,
  title: 'Begin Groep 6'
}, {
  tier: 2,
  levelCode: 'B6+',
  group: 6,
  period: 'B',
  isPlus: true,
  title: 'Begin Groep 6 (Plus)'
}, {
  tier: 3,
  levelCode: 'M6',
  group: 6,
  period: 'M',
  isPlus: false,
  title: 'Midden Groep 6'
}, {
  tier: 4,
  levelCode: 'M6+',
  group: 6,
  period: 'M',
  isPlus: true,
  title: 'Midden Groep 6 (Plus)'
}, {
  tier: 5,
  levelCode: 'E6',
  group: 6,
  period: 'E',
  isPlus: false,
  title: 'Eind Groep 6'
}, {
  tier: 6,
  levelCode: 'E6+',
  group: 6,
  period: 'E',
  isPlus: true,
  title: 'Eind Groep 6 (Plus)'
},
// GROEP 7 (tiers 7–12)
{
  tier: 7,
  levelCode: 'B7',
  group: 7,
  period: 'B',
  isPlus: false,
  title: 'Begin Groep 7'
}, {
  tier: 8,
  levelCode: 'B7+',
  group: 7,
  period: 'B',
  isPlus: true,
  title: 'Begin Groep 7 (Plus)'
}, {
  tier: 9,
  levelCode: 'M7',
  group: 7,
  period: 'M',
  isPlus: false,
  title: 'Midden Groep 7'
}, {
  tier: 10,
  levelCode: 'M7+',
  group: 7,
  period: 'M',
  isPlus: true,
  title: 'Midden Groep 7 (Plus)'
}, {
  tier: 11,
  levelCode: 'E7',
  group: 7,
  period: 'E',
  isPlus: false,
  title: 'Eind Groep 7'
}, {
  tier: 12,
  levelCode: 'E7+',
  group: 7,
  period: 'E',
  isPlus: true,
  title: 'Eind Groep 7 (Plus)'
},
// GROEP 8 (tiers 13–16, compressed: no B8+/M8+)
{
  tier: 13,
  levelCode: 'B8',
  group: 8,
  period: 'B',
  isPlus: false,
  title: 'Begin Groep 8'
}, {
  tier: 14,
  levelCode: 'M8',
  group: 8,
  period: 'M',
  isPlus: false,
  title: 'Midden Groep 8'
}, {
  tier: 15,
  levelCode: 'E8',
  group: 8,
  period: 'E',
  isPlus: false,
  title: 'Eind Groep 8 (1F)'
}, {
  tier: 16,
  levelCode: 'E8+',
  group: 8,
  period: 'E',
  isPlus: true,
  title: 'Groep 8 Plus (2F/S)'
}];

// ============================================================
// Lookup Maps (built once)
// ============================================================

const TIER_BY_NUMBER = new Map<number, TierConfig>(TIERS.map(t => [t.tier, t]));
const TIER_BY_LEVEL_CODE = new Map<string, TierConfig>(TIERS.map(t => [t.levelCode, t]));

// ============================================================
// Tier → NiveauCode helpers (canonical sync)
// ============================================================

/**
 * Get the niveauCode (levelCode) for a given tier number.
 * This is the canonical tier→niveau mapping.
 * Returns undefined for invalid tiers.
 */
export function tierToNiveauCode(tier: number): string | undefined {
  return TIER_BY_NUMBER.get(tier)?.levelCode;
}

/**
 * Get the tier number for a given niveauCode (levelCode).
 * This is the canonical niveau→tier mapping.
 * Returns undefined for invalid codes.
 */
export function niveauCodeToTier(niveauCode: string): number | undefined {
  return TIER_BY_LEVEL_CODE.get(niveauCode)?.tier;
}

/**
 * Validate that a tier and niveauCode are in sync.
 * Returns true if they match, false if mismatched.
 */
export function isTierNiveauSynced(tier: number, niveauCode: string): boolean {
  const expected = tierToNiveauCode(tier);
  return expected === niveauCode;
}

/**
 * Check if a tier number is valid (1–16).
 */
export function isValidTier(tier: number): boolean {
  return Number.isInteger(tier) && tier >= 1 && tier <= 16;
}

/**
 * Check if a niveauCode is valid.
 */
export function isValidNiveauCode(code: string): boolean {
  return TIER_BY_LEVEL_CODE.has(code);
}

// ============================================================
// Query helpers
// ============================================================

export const getTierConfig = (tier: number): TierConfig | undefined => {
  return TIER_BY_NUMBER.get(tier);
};
export const getTiersForGroup = (group: GroupId): TierConfig[] => {
  return TIERS.filter(t => t.group === group);
};
export const getTierLabel = (tier: number): string => {
  return getTierConfig(tier)?.levelCode || `Tier ${tier}`;
};
export const getTierTitle = (tier: number): string => {
  return getTierConfig(tier)?.title || `Niveau ${tier}`;
};
export const getAllTiers = (): TierConfig[] => TIERS;
export const MAX_TIER = 16;
export const MIN_TIER = 1;

// ============================================================
// Group ↔ Tier Gating (Canonical — Single Source of Truth)
// ============================================================

/**
 * Get the set of allowed tier numbers for a given group.
 * This is THE canonical function for group/tier gating.
 * All selection, fallback, and validation logic MUST use this.
 */
export function getAllowedTiersForGroup(group: GroupId): Set<number> {
  const range = getTierRangeForGroup(group);
  const allowed = new Set<number>();
  for (let t = range.min; t <= range.max; t++) {
    allowed.add(t);
  }
  return allowed;
}

/**
 * Check if a specific tier is allowed for a given group.
 */
export function isTierAllowedForGroup(tier: number, group: GroupId): boolean {
  const range = getTierRangeForGroup(group);
  return tier >= range.min && tier <= range.max;
}

/**
 * Clamp a tier number to the allowed range for a group.
 * If the tier is below the range, returns the minimum.
 * If above, returns the maximum.
 */
export function clampTierToGroup(tier: number, group: GroupId): number {
  const range = getTierRangeForGroup(group);
  return Math.max(range.min, Math.min(range.max, tier));
}

/**
 * Get the group number for a tier.
 */
export function getGroupForTier(tier: number): GroupId | undefined {
  return getTierConfig(tier)?.group as GroupId | undefined;
}

/**
 * Get the tier range for a group.
 */
export function getTierRangeForGroup(group: GroupId): {
  min: number;
  max: number;
} {
  switch (group) {
    case 6:
      return {
        min: 1,
        max: 6
      };
    case 7:
      return {
        min: 7,
        max: 12
      };
    case 8:
      return {
        min: 13,
        max: 16
      };
    default:
      return {
        min: 1,
        max: 16
      };
  }
}